import { useEffect, useState } from 'react';

import { useAuth0 } from '@auth0/auth0-react';
import { Box } from '@chakra-ui/react';
import jwt_decode from 'jwt-decode';
// eslint-disable-next-line no-restricted-imports
import { useLDClient } from 'launchdarkly-react-client-sdk';
import { useHistory, useLocation } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import ThunderOutboundCallModalManager from './components/Calls/ThunderOutboundCallModalManager';
import CurrentCallPanel from './components/CurrentCallPanel';
import ErrorBoundary from './components/ErrorBoundary/ErrorBoundary';
import Navbar from './components/Navbar';
import NotificationsHandler from './components/NotificationsHandler';
import PostCallSurveyModal from './components/PostCallSurveyModal/PostCallSurveyModal';
import TextWidget from './components/TextWidget';
import { ActiveConversation } from './components/TextWidget/Components/Message';

import Routes from './Routes';
import { PermissionEnum } from './constants/permissions';
import ROUTES from './constants/routes';
import { FiltersActionKind } from './globalFiltersUtils';
import useModal from './hooks/useModal';
import usePageTitle from './hooks/usePageTitle';
import { useUrlQueryParamsWithMultipleReducers } from './hooks/useUrlQueryParamsWithMultipleReducers';
import { CallsProvider } from './libs/callsContext';
import {
  AbilityContext,
  FiltersContext,
  InteractedContext,
  ModalContext,
  TextWidgetContext,
} from './libs/contextLib';
import { getAllowedDealSources } from './utils/permissions';

const App = () => {
  const { user, isLoading, isAuthenticated, getAccessTokenSilently } = useAuth0();
  const history = useHistory();
  const location = useLocation();

  const [abilities, setAbilities] = useState<Set<PermissionEnum>>(new Set());
  const [hasLoadedPermissions, setHasLoadedPermissions] = useState(false);
  const [interacted, setInteracted] = useState(false);
  const [textDealId, setTextDealId] = useState<number | null>(null);
  const [activeConversation, setActiveConversation] = useState<ActiveConversation>({
    role: '',
    phone_number: '',
    first_name: '',
    last_name: '',
  });
  const [unreadMessagesCount, setUnreadMessagesCount] = useState<number>(0);

  const { filters, dispatch } = useUrlQueryParamsWithMultipleReducers();
  const {
    global: { sources },
  } = filters;

  usePageTitle();

  const { modals, setModal, modalCloseCount } = useModal();

  useEffect(() => {
    const interactedFunc = () => {
      if (interacted) {
        document.removeEventListener('mousemove', interactedFunc);
      }
      setInteracted(true);
    };
    document.addEventListener('mousemove', interactedFunc);

    return () => {
      document.removeEventListener('mousemove', interactedFunc);
    };
  }, [interacted]);

  useEffect(() => {
    if (!isAuthenticated) {
      setHasLoadedPermissions(true);
      return;
    }

    const loadPermissions = async (): Promise<void> => {
      setHasLoadedPermissions(false);

      try {
        const data = await getAccessTokenSilently();
        const accessToken = jwt_decode<{
          permissions?: PermissionEnum[];
          'https://api.leaseend.com/role'?: string[];
        }>(data);

        setAbilities(new Set(accessToken.permissions ?? []));
      } finally {
        setHasLoadedPermissions(true);
      }
    };

    loadPermissions().catch(() => undefined);
  }, [isAuthenticated, getAccessTokenSilently]);

  useEffect(() => {
    if (sources.length === 0) {
      dispatch({
        type: FiltersActionKind.SET_SOURCES,
        payload: getAllowedDealSources(abilities),
      });
    }
  }, [abilities]);

  const canSeeDD1 = !abilities.has(PermissionEnum.CannotSeeDD1);

  useEffect(() => {
    if (!isAuthenticated || !hasLoadedPermissions || canSeeDD1) {
      return;
    }

    if (location.pathname !== ROUTES.MOVED_TO_DD2) {
      history.replace(ROUTES.MOVED_TO_DD2);
    }
  }, [canSeeDD1, hasLoadedPermissions, history, isAuthenticated, location.pathname]);

  const ldClient = useLDClient();

  useEffect(() => {
    if (user?.sub) {
      ldClient?.identify({
        kind: 'user',
        key: user.sub,
      });
    }
  }, [user?.sub]);

  const shouldShowAuthenticatedDD1Features = isAuthenticated && canSeeDD1;

  if (isAuthenticated && !hasLoadedPermissions) {
    return null;
  }

  if (!canSeeDD1 && location.pathname !== ROUTES.MOVED_TO_DD2) {
    return null;
  }

  return !isLoading ? (
    <Box maxW="100%">
      <CallsProvider>
        <FiltersContext.Provider value={{ filters, dispatch }}>
          {(sources || !isAuthenticated) && (
            <AbilityContext.Provider value={abilities}>
              <InteractedContext.Provider value={interacted}>
                <TextWidgetContext.Provider
                  value={{
                    textDealId,
                    setTextDealId,
                    activeConversation,
                    setActiveConversation,
                    unreadCount: unreadMessagesCount,
                    setUnreadCount: setUnreadMessagesCount,
                  }}
                >
                  <ModalContext.Provider value={{ modals, setModal, modalCloseCount }}>
                    <ErrorBoundary>
                      {canSeeDD1 && <Navbar isAuthenticated={isAuthenticated} />}
                      {shouldShowAuthenticatedDD1Features && <NotificationsHandler />}
                      {shouldShowAuthenticatedDD1Features && <TextWidget />}
                      <Routes />
                      {shouldShowAuthenticatedDD1Features && <CurrentCallPanel />}
                      {shouldShowAuthenticatedDD1Features && <PostCallSurveyModal />}
                      {shouldShowAuthenticatedDD1Features && <ThunderOutboundCallModalManager />}
                    </ErrorBoundary>
                  </ModalContext.Provider>
                </TextWidgetContext.Provider>
              </InteractedContext.Provider>
            </AbilityContext.Provider>
          )}
        </FiltersContext.Provider>
        <ToastContainer />
      </CallsProvider>
    </Box>
  ) : null;
};

export default App;
